import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Users, Edit, Trash2, Settings, Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

const clientSchema = z.object({
  name: z.string().min(1, 'Client name is required'),
  email: z.string().email('Valid email is required'),
  phone: z.string().optional(),
  domain: z.string().optional(),
  subdomain: z.string().optional(),
});

type ClientFormData = z.infer<typeof clientSchema>;
type Client = Database['public']['Tables']['clients']['Row'];

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitingClient, setInvitingClient] = useState<Client | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [businessPortal, setBusinessPortal] = useState<Client | null>(null);
  const [confirmProceed, setConfirmProceed] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      domain: '',
      subdomain: '',
    },
  });

  useEffect(() => {
    if (user) {
      fetchClients();
    }

    // listen for portal updates and refresh clients
    const listener = () => {
      if (user) fetchClients();
    };
    window.addEventListener('businessPortalUpdated', listener);
    return () => {
      window.removeEventListener('businessPortalUpdated', listener);
    };
  }, [user]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const allClients = data || [];
      const businessPortalClient = allClients.find(
        (client) => (client.settings as any)?.is_business_portal === true,
      );
      const regularClients = allClients.filter(
        (client) => (client.settings as any)?.is_business_portal !== true,
      );

      setBusinessPortal(businessPortalClient || null);
      setClients(regularClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        title: 'Error',
        description: 'Failed to load clients',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ClientFormData) => {
    try {
      if (editingClient) {
        const { data: updatedClient, error } = await supabase
          .from('clients')
          .update({
            name: data.name,
            email: data.email,
            phone: data.phone,
            domain: data.domain,
            subdomain: data.subdomain,
            // Preserve existing branding and settings when updating
            branding: editingClient.branding,
            settings: editingClient.settings,
          })
          .eq('id', editingClient.id)
          .eq('user_id', user?.id)
          .select()
          .single();

        if (error) {
          console.error('Supabase update error:', error);
          throw error;
        }

        // If the email changed (case-insensitive compare), try to update the
        // corresponding auth user so the client can sign in with the new address.
        // We only attempt this if there was a previous email value (so we know
        // which auth account to look up).  The update is done via a service‑role
        // edge function.
        const oldEmail = editingClient.email?.trim().toLowerCase() || '';
        const newEmail = data.email?.trim().toLowerCase() || '';
        if (oldEmail && oldEmail !== newEmail) {
          try {
            const { data: emailResp, error: emailErr } =
              await supabase.functions.invoke('update-client-user-email', {
                body: {
                  clientId: editingClient.id,
                  oldEmail: editingClient.email,
                  newEmail: data.email,
                },
              });
            if (emailErr) {
              console.error('Failed to sync auth email:', emailErr);
              toast({
                title: 'Warning',
                description:
                  'Client record updated but login email was not changed. The user must continue using their previous address.',
                variant: 'destructive',
              });
            } else {
              if (emailResp.userId) {
                toast({
                  title: 'Success',
                  description: 'Client login email updated successfully.',
                });
              } else {
                toast({
                  title: 'Success',
                  description:
                    'Client email updated. No login account found to update.',
                });
              }
            }
          } catch (e) {
            console.error('Error calling auth email sync function:', e);
            toast({
              title: 'Warning',
              description:
                'Client record updated but an error occurred while attempting to update the login email (see console).',
              variant: 'destructive',
            });
          }
        }

        // Update local state immediately with the updated client
        setClients((prevClients) =>
          prevClients.map((client) =>
            client.id === editingClient.id
              ? { ...client, ...updatedClient }
              : client,
          ),
        );

        toast({
          title: 'Success',
          description: 'Client updated successfully',
        });
      } else {
        const branding = businessPortal?.branding || {};

        const { data: newClient, error } = await supabase
          .from('clients')
          .insert({
            name: data.name,
            email: data.email,
            phone: data.phone,
            domain: data.domain,
            subdomain: data.subdomain,
            user_id: user?.id,
            status: 'active',
            branding: branding,
            settings: {
              inherits_business_branding: true,
            },
          })
          .select()
          .single();

        if (error) throw error;

        const { error: membershipError } = await supabase
          .from('client_memberships')
          .insert({
            user_id: user?.id,
            client_id: newClient.id,
            role: 'owner',
            invited_by: user?.id,
          });

        if (membershipError) {
          console.error('Error creating owner membership:', membershipError);
        }

        toast({
          title: 'Success',
          description: 'Client created successfully',
        });
      }

      setOpen(false);
      form.reset();

      // Only refetch if creating new client, already updated local state for edits
      if (!editingClient) {
        fetchClients();
      }

      setEditingClient(null);
    } catch (error: any) {
      console.error('Error saving client:', error);
      toast({
        title: 'Error',
        description: error.message.includes('clients_limit')
          ? 'You have reached the maximum number of clients for your license'
          : 'Failed to save client',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    form.reset({
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      domain: client.domain || '',
      subdomain: client.subdomain || '',
    });
    setOpen(true);
  };

  const handleDelete = async (clientId: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this client? This will also unassign all campaigns.',
      )
    ) {
      return;
    }

    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Client deleted successfully',
      });

      fetchClients();
    } catch (error: any) {
      console.error('Error deleting client:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete client',
        variant: 'destructive',
      });
    }
  };

  const handleInviteUser = (client: Client) => {
    setInvitingClient(client);
    setInviteOpen(true);
  };

  const handleSendInvitation = async () => {
    if (!invitingClient || !inviteEmail) return;

    try {
      const { data: businessPortalData, error: portalError } = await supabase
        .from('clients')
        .select('domain, subdomain')
        .eq('user_id', user?.id)
        .eq('settings->>is_business_portal', 'true')
        .maybeSingle();

      if (portalError) throw portalError;

      const hasCustomDomain =
        businessPortalData?.domain || businessPortalData?.subdomain;
      let verified = true; // Default to true if no custom domain

      if (hasCustomDomain) {
        const fullDomain =
          businessPortalData.domain ||
          `${businessPortalData.subdomain}.callsparker.com`;
        const type = businessPortalData.domain ? 'domain' : 'subdomain';
        const { data: verifyData, error: verifyError } =
          await supabase.functions.invoke('verify-custom-domain', {
            body: { domain: fullDomain, type: type },
          });

        if (verifyError) {
          console.error(
            '[handleSendInvitation] Verification error:',
            verifyError,
          );
          toast({
            title: 'Error',
            description:
              'Failed to check domain verification. Treating as unverified.',
            variant: 'destructive',
          });
          verified = false;
          setConfirmProceed(true);
          return;
        } else if (!verifyData?.verified) {
          verified = false;
          setConfirmProceed(true);
          return;
        }
      }

      await sendInvite();
    } catch (error: any) {
      console.error('[handleSendInvitation] Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to prepare invitation',
        variant: 'destructive',
      });
    }
  };

  const sendInvite = async () => {
    try {
      const inviteData = {
        email: inviteEmail,
        client_id: invitingClient?.id,
        role: 'member',
      };

      const { data, error } = await supabase.functions.invoke(
        'invite-client-user',
        {
          body: inviteData,
        },
      );

      if (error) throw error;

      toast({
        title: 'Success',
        description: data.message || `Invitation sent to ${inviteEmail}`,
      });

      setInviteOpen(false);
      setInviteEmail('');
      setInvitingClient(null);
      setConfirmProceed(false);
    } catch (error: any) {
      console.error('[sendInvite] Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to send invitation',
        variant: 'destructive',
      });
      setConfirmProceed(false);
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    form.reset();
  };

  const generateAccessLink = (client: Client) => {
    const baseUrl = businessPortal?.domain
      ? `https://${businessPortal.domain}`
      : businessPortal?.subdomain
        ? `https://${businessPortal.subdomain}.callsparker.com`
        : `${window.location.origin}`;

    return `${baseUrl}/portal/client/${client.id}/actions`;
  };

  const copyAccessLink = (client: Client) => {
    navigator.clipboard.writeText(generateAccessLink(client));
    toast({
      title: 'Success',
      description: 'Access link copied to clipboard',
    });
  };

  if (loading) {
    return (
      <div className='container mx-auto p-6 space-y-8'>
        <div>
          <h1 className='text-3xl font-bold text-foreground'>
            Client Management
          </h1>
          <p className='text-muted-foreground'>
            Manage your clients and their portal access
          </p>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {[1, 2, 3].map((i) => (
            <Card key={i} className='animate-pulse'>
              <CardHeader>
                <div className='h-4 bg-muted rounded w-3/4'></div>
                <div className='h-3 bg-muted rounded w-1/2'></div>
              </CardHeader>
              <CardContent>
                <div className='h-20 bg-muted rounded'></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='p-6 space-y-8'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-foreground'>
            Client Management
          </h1>
          <p className='text-muted-foreground'>
            Manage your clients and their portal access
          </p>
        </div>

        <div className='flex items-center gap-2'>
          {businessPortal && (
            <Button
              variant='outline'
              onClick={() => navigate('/business-portal')}
            >
              <Settings className='h-4 w-4 mr-2' />
              Business Portal
            </Button>
          )}

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} disabled={!businessPortal}>
                <Plus className='h-4 w-4 mr-2' />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className='max-w-md'>
              <DialogHeader>
                <DialogTitle>
                  {editingClient ? 'Edit Client' : 'Add New Client'}
                </DialogTitle>
              </DialogHeader>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className='space-y-4'
                >
                  <FormField
                    control={form.control}
                    name='name'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Name</FormLabel>
                        <FormControl>
                          <Input placeholder='Acme Corp' {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='email'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            type='email'
                            placeholder='contact@acme.com'
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='phone'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder='+1 555-0123'
                            {...field}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow digits, +, -, (, ), and spaces only
                              if (/^[+]?[0-9\-() ]*$/.test(value)) {
                                field.onChange(value);
                              }
                            }}
                            type='tel' // Optional: Improves mobile keyboard, but doesn't restrict chars
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className='flex justify-end space-x-2 pt-4'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type='submit'>
                      {editingClient ? 'Update Client' : 'Create Client'}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Invitation Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Invite User to {invitingClient?.name}</DialogTitle>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='invite-email'>Email Address</Label>
              <Input
                id='invite-email'
                type='email'
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder='user@example.com'
              />
            </div>

            <div className='flex justify-end space-x-2 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setInviteOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSendInvitation} disabled={!inviteEmail}>
                Send Invitation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Proceed Dialog for unverified domain */}
      <Dialog open={confirmProceed} onOpenChange={setConfirmProceed}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Custom Domain Not Verified</DialogTitle>
          </DialogHeader>
          <p className='text-muted-foreground'>
            Your custom domain/subdomain is not yet verified. Do you wish to
            proceed and send the invitation using the default CallSparker
            domain, or cancel and retry later after verification?
          </p>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmProceed(false)}>
              Cancel
            </Button>
            <Button onClick={sendInvite}>Proceed with Default</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Grid */}
      {clients.length === 0 ? (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <Building2 className='h-12 w-12 text-muted-foreground mb-4' />
            <h3 className='text-lg font-medium text-foreground mb-2'>
              No clients yet
            </h3>
            <p className='text-muted-foreground text-center mb-4'>
              {!businessPortal
                ? 'Set up your Business Portal first, then create your first client'
                : 'Create your first client to start managing campaigns for them'}
            </p>
            {!businessPortal ? (
              <Button onClick={() => navigate('/business-portal')}>
                <Settings className='h-4 w-4 mr-2' />
                Setup Business Portal
              </Button>
            ) : (
              <Button onClick={() => setOpen(true)}>
                <Plus className='h-4 w-4 mr-2' />
                Add Your First Client
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {clients.map((client) => (
            <Card
              key={client.id}
              className='group hover:shadow-md transition-shadow'
            >
              <CardHeader>
                <div className='flex items-start justify-between'>
                  <div>
                    <CardTitle className='flex items-center gap-2'>
                      <Building2 className='h-5 w-5' />
                      {client.name}
                    </CardTitle>
                    <CardDescription>{client.email}</CardDescription>
                  </div>
                  <Badge
                    variant={
                      client.status === 'active' ? 'default' : 'secondary'
                    }
                  >
                    {client.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className='space-y-2 text-sm text-muted-foreground'>
                  {client.phone && (
                    <div className='flex items-center gap-2'>
                      <span>📞</span>
                      <span>{client.phone}</span>
                    </div>
                  )}
                  {client.domain && (
                    <div className='flex items-center gap-2'>
                      <span>🌐</span>
                      <span>{client.domain}</span>
                    </div>
                  )}
                  {client.subdomain && (
                    <div className='flex items-center gap-2'>
                      <span>🏷️</span>
                      <span>{client.subdomain}</span>
                    </div>
                  )}
                  <div className='flex items-center gap-2'>
                    <span>📅</span>
                    <span>
                      Created {new Date(client.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className='flex flex-wrap gap-2 mt-4'>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => handleEdit(client)}
                    className='flex-1 min-w-0'
                  >
                    <Edit className='h-3 w-3 mr-1' />
                    Edit
                  </Button>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => handleInviteUser(client)}
                    className='flex-1 min-w-0'
                  >
                    <Users className='h-3 w-3 mr-1' />
                    Invite
                  </Button>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => copyAccessLink(client)}
                    className='flex-1 min-w-0'
                  >
                    <Building2 className='h-3 w-3 mr-1' />
                    Link
                  </Button>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => handleDelete(client.id)}
                    className='text-destructive hover:text-destructive'
                  >
                    <Trash2 className='h-3 w-3' />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
